import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useCreateOrUpdateProfile } from "./useCreateOrUpdateProfile";
import { useProfileValidation } from "./useProfileValidation";
import { useEnvironmentAwareSMSAuth } from "./useEnvironmentAwareSMSAuth";
import { supabase } from "@/integrations/supabase/client";

export const useSMSSignUp = () => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { createOrUpdateProfile } = useCreateOrUpdateProfile();
  const { validationError, setValidationError, validateDisplayName } = useProfileValidation();
  const { signUpSMSInviteWithoutVerification } = useEnvironmentAwareSMSAuth();

  const handleSMSSignUp = async (
    email: string, 
    password: string, 
    confirmPassword: string, 
    displayName: string,
    phoneNumber: string,
    inviteRole: string,
    token: string | null,
    e: React.FormEvent
  ): Promise<void> => {
    e.preventDefault();
    
    if (!validateDisplayName(displayName)) {
      return;
    }
    
    if (password !== confirmPassword) {
      setValidationError("Passwords do not match");
      return;
    }
    
    try {
      setLoading(true);
      setValidationError(""); // Clear any previous validation errors
      
      const finalDisplayName = displayName.trim();
      console.log("[SMSSignUp] Starting SMS invitation signup for:", { email, phoneNumber, inviteRole });
      
      const { data, error: signUpError } = await signUpSMSInviteWithoutVerification(
        email,
        password,
        {
          displayName: finalDisplayName,
          role: inviteRole,
          phoneNumber: phoneNumber
        }
      );

      if (signUpError) {
        console.error("[SMSSignUp] Signup error:", signUpError);
        
        let errorMessage = signUpError.message;
        
        if (signUpError.message.includes("already registered")) {
          errorMessage = "This email is already registered. Please use a different email or contact support.";
        } else if (signUpError.message.includes("weak password")) {
          errorMessage = "Please choose a stronger password with a mix of letters, numbers, and special characters.";
        }
        
        setValidationError(errorMessage);
        throw new Error(errorMessage);
      }

      console.log("[SMSSignUp] Signup successful, user data:", data);

      if (data?.user) {
        // Create the user profile
        try {
          await createOrUpdateProfile(data.user.id, {
            display_name: finalDisplayName,
            email: email
          });
          console.log("[SMSSignUp] Profile created successfully");
        } catch (profileError) {
          console.error("[SMSSignUp] Profile creation failed:", profileError);
          // Don't fail the entire process for profile creation issues
        }

        // Claim the secure invitation using token
        if (token) {
          console.log("[SMSSignUp] Attempting to claim secure invitation with token");
          try {
            const userAgent = navigator.userAgent;
            const { data: claimResult, error: claimError } = await supabase.rpc('claim_secure_invitation', {
              p_token: token,
              p_user_id: data.user.id,
              p_user_agent: userAgent
            });
            
            if (claimError) {
              console.error("[SMSSignUp] Failed to claim secure invitation:", claimError);
              toast({
                variant: "destructive",
                title: "Warning",
                description: "Account created but invitation claiming failed. Please contact support.",
              });
            } else if (claimResult && claimResult.length > 0 && claimResult[0].success) {
              console.log("[SMSSignUp] Secure invitation claimed successfully");
              toast({
                title: "Role Assigned!",
                description: `Your ${claimResult[0].role_assigned} role has been activated.`,
              });
            } else {
              console.log("[SMSSignUp] Failed to claim secure invitation");
              toast({
                variant: "destructive",
                title: "Warning", 
                description: "Account created but invitation claiming failed. Please contact support.",
              });
            }
          } catch (inviteError) {
            console.error("[SMSSignUp] Error claiming secure invitation:", inviteError);
            toast({
              variant: "destructive",
              title: "Warning",
              description: "Account created but invitation processing failed. Please contact support.",
            });
          }
        } else if (phoneNumber && inviteRole) {
          // Fallback to legacy phone number-based claiming
          console.log("[SMSSignUp] Attempting to claim legacy SMS invitation for:", phoneNumber);
          try {
            const { data: claimResult, error: claimError } = await supabase.rpc('claim_sms_invitation', {
              p_phone_number: phoneNumber,
              p_user_id: data.user.id,
              p_role: inviteRole
            });
            
            if (claimError) {
              console.error("[SMSSignUp] Failed to claim legacy SMS invitation:", claimError);
              toast({
                variant: "destructive",
                title: "Warning",
                description: "Account created but invitation claiming failed. Please contact support.",
              });
            } else if (claimResult) {
              console.log("[SMSSignUp] Legacy SMS invitation claimed successfully");
            } else {
              console.log("[SMSSignUp] No matching legacy SMS invitation found");
              toast({
                variant: "destructive",
                title: "Warning", 
                description: "Account created but no matching invitation found. Please contact support.",
              });
            }
          } catch (inviteError) {
            console.error("[SMSSignUp] Error claiming legacy SMS invitation:", inviteError);
            toast({
              variant: "destructive",
              title: "Warning",
              description: "Account created but invitation processing failed. Please contact support.",
            });
          }
        }
      }

      toast({
        title: "Account Created Successfully!",
        description: `Welcome! Your ${inviteRole} account is ready to use. No email verification required.`,
      });
      
      // For SMS invitations, we can redirect immediately since no email verification is needed
      setTimeout(() => {
        window.location.href = "/";
      }, 2000);
      
    } catch (error: any) {
      console.error("[SMSSignUp] Error during SMS signup:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    handleSMSSignUp,
    loading,
    validationError,
    setValidationError,
    validateDisplayName
  };
};